/*****************************************************************
 *  CUSTOMER BUTLER - DATES AND TIMES ON SCREEN                    *
 *  File: apps/web/src/pages/customer/customerDates.js             *
 *  What it does: the ONE place the customer pages turn a stored   *
 *  date or time into words, always in the viewer's OWN local      *
 *  time - never the raw UTC text the database keeps.              *
 *  Reads: nothing.  Writes: nothing.  Pure functions.             *
 *****************************************************************/

/* WHY (2026-10-14, James; readiness defect): the old customer pages showed
   raw times ("2026-10-13T02:00:00.000Z"). A person reads their own clock.
   Two kinds of value are kept apart on purpose:
     1. a DATE ONLY ("2026-10-12", e.g. Last Water Collection Training
        Date) is a calendar day; it must show as Oct 12 everywhere and
        must never slide to Oct 11 because of a time zone;
     2. a MOMENT ("2026-10-12T23:11:07-04:00", "...Z") is converted to the
        viewer's local time. */

/*****************************************************************
 *  SECTION 1 - TELLING THE TWO KINDS APART                        *
 *****************************************************************/

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/; // 2026-10-12, nothing after

/* ---- isDateOnlyText(value) -- true for "YYYY-MM-DD" exactly ---- */
export function isDateOnlyText(value) {
  const looksLikeDateOnly = typeof value === 'string' && DATE_ONLY_PATTERN.test(value.trim());

  return looksLikeDateOnly;
}

/*****************************************************************
 *  SECTION 2 - THE WORDS                                          *
 *****************************************************************/

const DAY_WORDS = { year: 'numeric', month: 'short', day: 'numeric' };                // Oct 12, 2026
const MOMENT_WORDS = { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };

/* ---- formatDateOnly(value) -------------------------------------
   "2026-10-12" -> "Oct 12, 2026", built from the three numbers so
   no time zone can move the day. Anything else is handed to
   formatMoment. Empty in, empty out.                            */
export function formatDateOnly(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (!isDateOnlyText(value)) {
    return formatMoment(value);
  }

  const dateParts = DATE_ONLY_PATTERN.exec(value.trim());
  const yearNumber = Number(dateParts[1]);
  const monthIndex = Number(dateParts[2]) - 1;
  const dayNumber = Number(dateParts[3]);
  const localCalendarDay = new Date(yearNumber, monthIndex, dayNumber); // local midnight of that day

  return localCalendarDay.toLocaleDateString(undefined, DAY_WORDS);
}

/* ---- formatMoment(value) ---------------------------------------
   A moment in the viewer's local time: "Oct 12, 2026, 11:11 PM".
   In: an ISO text, a number of ms, or a Date.
   Out: words; a value that is not a time comes back as its own
   text (so nothing is ever hidden); empty in, empty out.        */
export function formatMoment(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (isDateOnlyText(value)) {
    return formatDateOnly(value);
  }

  const momentDate = value instanceof Date ? value : new Date(value);
  const isRealTime = !Number.isNaN(momentDate.getTime());

  if (!isRealTime) {
    return String(value);
  }

  return momentDate.toLocaleString(undefined, MOMENT_WORDS);
}

/* ---- formatDay(value) ------------------------------------------
   Only the calendar day of a moment, in local time: "Oct 12, 2026".
   For lists where the minute does not matter.                   */
export function formatDay(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (isDateOnlyText(value)) {
    return formatDateOnly(value);
  }

  const momentDate = value instanceof Date ? value : new Date(value);
  const isRealTime = !Number.isNaN(momentDate.getTime());

  if (!isRealTime) {
    return String(value);
  }

  return momentDate.toLocaleDateString(undefined, DAY_WORDS);
}

/* ---- toLocalInputValue(value) ----------------------------------
   A moment as the "YYYY-MM-DDTHH:MM" a datetime box wants, in the
   viewer's local time (so editing never shifts the time).       */
export function toLocalInputValue(value) {
  if (!value) {
    return '';
  }

  const momentDate = new Date(value);

  if (Number.isNaN(momentDate.getTime())) {
    return '';
  }

  const offsetMs = momentDate.getTimezoneOffset() * 60 * 1000;
  const localClock = new Date(momentDate.getTime() - offsetMs);

  return localClock.toISOString().slice(0, 16);
}

/* ---- fromLocalInputValue(boxValue) -----------------------------
   The datetime box's local "YYYY-MM-DDTHH:MM" back to a true ISO
   moment for storage. Empty in, empty out.                      */
export function fromLocalInputValue(boxValue) {
  if (!boxValue) {
    return '';
  }

  const localMoment = new Date(boxValue); // the browser reads a bare date-time as local

  if (Number.isNaN(localMoment.getTime())) {
    return '';
  }

  return localMoment.toISOString();
}
